import { ReactNode, useEffect, useMemo, useState } from 'react';
import { useAsync } from 'react-use';

import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';

import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

const MAX_OPTIONS = 50;
const MIN_SEARCH_LENGTH = 2;

interface RepoOption {
  id: string;
  url: string;
  name: string;
  isExternal: boolean;
  isPlaceholder: boolean;
}

const PLACEHOLDER_OPTION: RepoOption = {
  id: 'external:placeholder',
  url: '',
  name: '',
  isExternal: true,
  isPlaceholder: true,
};

export interface RepoUrlPickerProps {
  value: string;
  onChange: (value: string) => void;
  label: string;
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  helperText?: ReactNode;
  freeSolo: boolean;
  catalogFilter?: (option: { url: string; name: string }) => boolean;
  endAdornment?: ReactNode;
}

export const RepoUrlPicker = ({
  value,
  onChange,
  label,
  required = false,
  disabled = false,
  error = false,
  helperText,
  freeSolo,
  catalogFilter,
  endAdornment,
}: RepoUrlPickerProps) => {
  const [inputValue, setInputValue] = useState<string>(value || '');

  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  const catalogApi = useApi(catalogApiRef);
  const { value: repoEntities, loading } = useAsync(
    () => catalogApi.getEntities({ filter: { kind: 'Repository' } }),
    [catalogApi],
  );

  const allCatalogRepos = useMemo<RepoOption[]>(() => {
    const items = repoEntities?.items ?? [];
    return items
      .map(entity => {
        const slug =
          entity.metadata.annotations?.['github.com/project-slug'] ?? '';
        return {
          id: `${entity.kind}:${entity.metadata.namespace ?? 'default'}/${
            entity.metadata.name
          }`,
          url: slug ? `https://github.com/${slug}` : '',
          name: entity.metadata.name,
          isExternal: false,
          isPlaceholder: false,
        };
      })
      .filter(r => r.url)
      .filter(r => (catalogFilter ? catalogFilter(r) : true));
  }, [repoEntities, catalogFilter]);

  const options = useMemo<RepoOption[]>(() => {
    const trimmed = inputValue.trim();
    let pool = allCatalogRepos;
    if (trimmed.length >= MIN_SEARCH_LENGTH) {
      const q = trimmed.toLowerCase();
      pool = pool.filter(
        r =>
          r.name.toLowerCase().includes(q) || r.url.toLowerCase().includes(q),
      );
    }
    pool = pool.slice(0, MAX_OPTIONS);

    if (!freeSolo) return pool;

    const externalRow: RepoOption = trimmed
      ? {
          id: `external:${trimmed}`,
          url: trimmed,
          name: trimmed,
          isExternal: true,
          isPlaceholder: false,
        }
      : PLACEHOLDER_OPTION;
    return [externalRow, ...pool];
  }, [allCatalogRepos, inputValue, freeSolo]);

  const selectedOption = useMemo<RepoOption | null>(() => {
    if (!value) return null;
    const matched = allCatalogRepos.find(r => r.url === value);
    if (matched) return matched;
    if (freeSolo) {
      return {
        id: `external:${value}`,
        url: value,
        name: value,
        isExternal: true,
        isPlaceholder: false,
      };
    }
    return null;
  }, [value, allCatalogRepos, freeSolo]);

  const handleBlur = () => {
    if (!freeSolo) return;
    const typed = inputValue.trim();
    if (!typed) return;
    const looksLikeHost =
      !typed.includes('://') &&
      typed.includes('.') &&
      !typed.includes('{{') &&
      !typed.includes('}}');
    if (looksLikeHost) {
      const withScheme = `https://${typed}`;
      setInputValue(withScheme);
      if (withScheme !== value) onChange(withScheme);
      return;
    }
    if (typed !== value) onChange(typed);
  };

  return (
    <Autocomplete<RepoOption, false, false, boolean>
      size="small"
      freeSolo={freeSolo}
      disabled={disabled}
      options={options}
      value={selectedOption}
      loading={loading}
      filterOptions={x => x}
      groupBy={option =>
        option.isExternal ? 'External URL' : 'TomTom Internal Repositories'
      }
      getOptionLabel={option =>
        typeof option === 'string' ? option : option.url
      }
      isOptionEqualToValue={(option, val) => option.url === val.url}
      getOptionDisabled={option => option.isPlaceholder}
      onInputChange={(_, v) => {
        // Sync local input state for every reason — including 'clear' (X icon),
        // otherwise blur reads the stale value and writes it back to the parent.
        setInputValue(v);
      }}
      onChange={(_, picked) => {
        if (picked === null) {
          onChange('');
          return;
        }
        if (typeof picked === 'string') {
          onChange(picked);
          return;
        }
        if (picked.isPlaceholder) return; // disabled row — never commits
        onChange(picked.url);
        setInputValue(picked.url);
      }}
      renderOption={({ key, ...optionProps }, option) => {
        if (option.isPlaceholder) {
          return (
            <li key={key} {...optionProps}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Start typing to use an External URL
                </Typography>
              </Box>
            </li>
          );
        }
        if (option.isExternal) {
          return (
            <li key={key} {...optionProps}>
              <Box>
                <Typography variant="body2" style={{fontWeight: 600}}>
                  Use &quot;{option.url}&quot;
                </Typography>
              </Box>
            </li>
          );
        }
        return (
          <li key={key} {...optionProps}>
            <Box>
              <Typography variant="body2" style={{fontWeight: 600}} noWrap>
                {option.name}
              </Typography>
              <Typography variant="caption" noWrap color="text.secondary">
                {option.url}
              </Typography>
            </Box>
          </li>
        );
      }}
      renderInput={params => (
        <TextField
          {...params}
          label={label}
          required={required}
          variant="outlined"
          margin="none"
          size="small"
          fullWidth
          error={error}
          helperText={helperText}
          onBlur={handleBlur}
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {endAdornment}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
    />
  );
};
